import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { CurrentUserSecurity } from '../Types/Security';
import { getCurrentUserSecurity } from '../Services/SecurityService';
import { useAuth } from './AuthProvider';

interface LocationCandidate {
  locationId?: string | null;
  locationName?: string | null;
}

interface SecurityContextType {
  security: CurrentUserSecurity | null;
  isSecurityLoading: boolean;
  securityError: Error | null;
  refreshSecurity: () => Promise<void>;
  hasRole: (roleName: string) => boolean;
  hasAnyRole: (roleNames: string[]) => boolean;
  hasPermission: (permissionName: string) => boolean;
  hasAnyPermission: (permissionNames: string[]) => boolean;
  hasPermissionForLocation: (permissionName: string, locationId: string) => boolean;
  getDefaultIncidentLocationCandidate: () => LocationCandidate | null;
}

const SecurityContext = createContext<SecurityContextType | undefined>(undefined);

export const SecurityProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const { isSignedIn } = useAuth();
  const [security, setSecurity] = useState<CurrentUserSecurity | null>(null);
  const [isSecurityLoading, setIsSecurityLoading] = useState(false);
  const [securityError, setSecurityError] = useState<Error | null>(null);
  const fetchedRef = useRef(false);

  const loadSecurity = useCallback(async () => {
    setIsSecurityLoading(true);
    setSecurityError(null);
    try {
      const data = await getCurrentUserSecurity();
      setSecurity(data);
    } catch (err: unknown) {
      setSecurityError(err instanceof Error ? err : new Error('Failed to load security metadata'));
    } finally {
      setIsSecurityLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSignedIn && !fetchedRef.current) {
      fetchedRef.current = true;
      loadSecurity();
    }
  }, [isSignedIn, loadSecurity]);

  const refreshSecurity = useCallback(async () => {
    await loadSecurity();
  }, [loadSecurity]);

  const hasRole = useCallback((roleName: string): boolean => {
    if (!security) return false;
    return security.roleNames.some((r) => r.toLowerCase() === roleName.toLowerCase());
  }, [security]);

  const hasAnyRole = useCallback((roleNames: string[]): boolean => {
    return roleNames.some((r) => hasRole(r));
  }, [hasRole]);

  const hasPermission = useCallback((permissionName: string): boolean => {
    if (!security) return false;
    return security.permissionNames.some((p) => p.toLowerCase() === permissionName.toLowerCase());
  }, [security]);

  const hasAnyPermission = useCallback((permissionNames: string[]): boolean => {
    return permissionNames.some((p) => hasPermission(p));
  }, [hasPermission]);

  const hasPermissionForLocation = useCallback((permissionName: string, locationId: string): boolean => {
    if (!security) return false;
    const perm = security.permissions.find(
      (p) => p.permissionName.toLowerCase() === permissionName.toLowerCase(),
    );
    if (!perm) return false;
    if (perm.isGlobal) return true;
    return perm.locationIds.includes(locationId);
  }, [security]);

  const getDefaultIncidentLocationCandidate = useCallback((): LocationCandidate | null => {
    if (!security) return null;

    // 1. Exactly one role with locationId and Incident.Create permission
    const createRoles = security.roles.filter(
      (r) => r.locationId && r.permissionNames.some((p) => p.toLowerCase() === 'incident.create'),
    );
    if (createRoles.length === 1) {
      return { locationId: createRoles[0].locationId, locationName: createRoles[0].locationName };
    }

    // 2. Exactly one Incident.Create permission with exactly one non-null locationId
    const createPerm = security.permissions.find(
      (p) => p.permissionName.toLowerCase() === 'incident.create',
    );
    if (createPerm && !createPerm.isGlobal) {
      const nonNullLocIds = createPerm.locationIds.filter((id): id is string => id != null);
      if (nonNullLocIds.length === 1) {
        const matchingRole = security.roles.find((r) => r.locationId === nonNullLocIds[0]);
        return { locationId: nonNullLocIds[0], locationName: matchingRole?.locationName ?? null };
      }
    }

    // 3. Exactly one location-scoped role with locationId
    const locationRoles = security.roles.filter((r) => r.scope === 'location' && r.locationId);
    if (locationRoles.length === 1) {
      return { locationId: locationRoles[0].locationId, locationName: locationRoles[0].locationName };
    }

    // 4. Multiple or no clear location
    return null;
  }, [security]);

  return (
    <SecurityContext.Provider
      value={{
        security,
        isSecurityLoading,
        securityError,
        refreshSecurity,
        hasRole,
        hasAnyRole,
        hasPermission,
        hasAnyPermission,
        hasPermissionForLocation,
        getDefaultIncidentLocationCandidate,
      }}
    >
      {children}
    </SecurityContext.Provider>
  );
};

export const useSecurity = (): SecurityContextType => {
  const context = useContext(SecurityContext);
  if (!context) throw new Error('useSecurity must be used within a SecurityProvider');
  return context;
};
